/**
 * Framework-free connection engine for the member-portal realtime socket
 * (#172 / #182 / #189). Owns the WebSocket lifecycle: connect, capped-backoff
 * reconnect, JSON frame dispatch, and the staleness watchdog.
 *
 * Why the watchdog (#189): the backend sends a keepalive ping every 20 s
 * (``PortalConsumer._heartbeat``, #182), so a *live* socket is never silent
 * for long. But a half-open socket — the path died without a close frame
 * ever reaching us (proxy/NAT idle kill, hard backend restart) — looks
 * exactly like a healthy idle socket to the browser: readyState OPEN, no
 * ``onclose``, no frames. Every server push fired at it is silently lost,
 * and because ``onclose`` never fires, the reconnect catch-up path never
 * runs either. The watchdog turns the server heartbeat into something the
 * client actually *checks*: if no frame at all (pings count) arrives within
 * ``STALE_AFTER_MS``, the socket is declared dead and force-closed, which
 * drives the normal reconnect → ``"reconnect"`` catch-up dispatch.
 *
 * Kept free of React/DOM so it unit-tests in node with fake timers; the
 * ``PortalSocketProvider`` wires it to React context and the real WebSocket.
 */

export interface PortalEvent {
  type: string;
  [key: string]: unknown;
}

/** Built-in event type dispatched on every successful reopen after the first. */
export const RECONNECT_EVENT = "reconnect";

/** Silence longer than this marks the socket dead (server pings every 20 s —
 *  55 s means ~2.5 missed pings, comfortably past jitter but well under the
 *  session-revalidation poll, so push loss stays bounded and short). */
export const STALE_AFTER_MS = 55_000;

/** How often the watchdog checks for staleness. */
export const STALE_CHECK_MS = 10_000;

/** The subset of the WebSocket API the engine drives (swap-in-able in tests). */
export interface EngineSocket {
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: (() => void) | null;
  onclose: (() => void) | null;
  close(): void;
}

export interface PortalSocketEngineOptions {
  /** Returns the WS url, or null when no token is available yet (mid-login). */
  resolveUrl: () => string | null;
  createSocket: (url: string) => EngineSocket;
  /** Receives every parsed event frame, plus the synthetic reconnect event. */
  dispatch: (event: PortalEvent) => void;
  staleAfterMs?: number;
  staleCheckMs?: number;
  now?: () => number;
}

export interface PortalSocketEngine {
  stop: () => void;
}

export function startPortalSocketEngine(opts: PortalSocketEngineOptions): PortalSocketEngine {
  const staleAfterMs = opts.staleAfterMs ?? STALE_AFTER_MS;
  const staleCheckMs = opts.staleCheckMs ?? STALE_CHECK_MS;
  const now = opts.now ?? Date.now;

  let stopped = false;
  let socket: EngineSocket | null = null;
  let attempt = 0;
  let hasConnectedBefore = false;
  let lastFrameAt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setInterval> | null = null;

  const clearWatchdog = () => {
    if (watchdog !== null) {
      clearInterval(watchdog);
      watchdog = null;
    }
  };

  const scheduleRetry = () => {
    if (stopped || retryTimer !== null) return;
    attempt += 1;
    // 1s → 2s → 4s → 8s → 16s → 30s (capped), plus up to 30% jitter.
    const base = Math.min(30_000, 1_000 * 2 ** Math.min(attempt - 1, 5));
    const jitter = Math.random() * 0.3 * base;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      connect();
    }, base + jitter);
  };

  /** Detach + close the current socket so a late onclose can't double-retry. */
  const dropSocket = () => {
    clearWatchdog();
    const ws = socket;
    if (!ws) return;
    socket = null;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      ws.close();
    } catch {
      // Already dead — closing was the point.
    }
  };

  const onStaleCheck = () => {
    if (now() - lastFrameAt <= staleAfterMs) return;
    // Half-open socket (#189): the 20 s server heartbeat has gone silent for
    // longer than any healthy socket ever would. Declare it dead and
    // reconnect — the reconnect dispatch triggers the catch-up refetches.
    dropSocket();
    scheduleRetry();
  };

  const connect = () => {
    if (stopped) return;
    const url = opts.resolveUrl();
    if (!url) {
      // No token yet (mid-login) — retry shortly under the same backoff.
      scheduleRetry();
      return;
    }
    const ws = opts.createSocket(url);
    socket = ws;

    ws.onopen = () => {
      attempt = 0;
      lastFrameAt = now();
      clearWatchdog();
      watchdog = setInterval(onStaleCheck, staleCheckMs);
      // Catch-up refetch on RECONNECT only — the first connect coincides
      // with the app's initial queries already loading.
      if (hasConnectedBefore) {
        opts.dispatch({ type: RECONNECT_EVENT });
      }
      hasConnectedBefore = true;
    };

    ws.onmessage = (ev) => {
      lastFrameAt = now(); // any frame (keepalive pings included) proves liveness
      let msg: PortalEvent | null = null;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (msg && typeof msg.type === "string") opts.dispatch(msg);
    };

    ws.onerror = () => {
      // onerror fires before onclose; let onclose schedule the retry.
    };

    ws.onclose = () => {
      if (socket === ws) socket = null;
      clearWatchdog();
      scheduleRetry();
    };
  };

  connect();

  return {
    stop() {
      stopped = true;
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      dropSocket();
    },
  };
}
